import { useMemo } from 'react';
import { OrderedMap } from 'immutable';
import { useQuery } from 'data-fetching-client';
import I18n from 'I18n';
import UIIcon from 'UIComponents/icon/UIIcon';
import { WORKFLOWS_ALLOWED_CUSTOM_OBJECT_META_TYPE_IDS, getMetaTypeStringFromObjectTypeId, getTypeIdFromObjectTypeId } from 'automation-ui-references/object-types/objectTypeUtils';
import { INTEGRATION } from 'automation-ui-references/object-types/InboundDbMetaTypeIds';
import { GET_ALL_OBJECT_TYPES } from 'automation-ui-enrollment/queries/ObjectTypesQueries';
import { GET_ENROLLABLE_OBJECT_TYPES_FAS } from 'automation-ui-enrollment/queries/EnrollableWorkflowTypesQueries';
import useAccessByObjectTypeId from 'automation-ui-enrollment/hooks/useAccessByObjectTypeId';
import { getHubSpotDefinedObjectTypeConfig, getCustomObjectTypeConfig, getAppObjectTypeConfig } from 'automation-ui-enrollment/components/workflowType/constants/WorkflowTypeConfig';
import { jsx as _jsx } from "react/jsx-runtime";
function compareObjectTypeIds(objectTypeIdA, objectTypeIdB) {
  const metaTypeIdA = getMetaTypeStringFromObjectTypeId(objectTypeIdA);
  const metaTypeIdB = getMetaTypeStringFromObjectTypeId(objectTypeIdB);
  if (metaTypeIdA < metaTypeIdB) {
    return -1;
  } else if (metaTypeIdA > metaTypeIdB) {
    return 1;
  }
  const typeIdA = getTypeIdFromObjectTypeId(objectTypeIdA);
  const typeIdB = getTypeIdFromObjectTypeId(objectTypeIdB);
  if (typeIdA < typeIdB) {
    return -1;
  } else if (typeIdA > typeIdB) {
    return 1;
  } else {
    return 0;
  }
}
const EMPTY_OBJECT_TYPES = OrderedMap();
const EMPTY_ENROLLABLE_OBJECT_TYPE = {};
const DEFAULT_OBJECT_TYPE_FILTER = () => true;
export default function useWorkflowTypeOptions({
  objectTypeFilter = DEFAULT_OBJECT_TYPE_FILTER
} = {}) {
  const {
    data: accessByObjectTypeId,
    loading: loadingAccess
  } = useAccessByObjectTypeId();
  const {
    data: objectTypeData,
    loading: loadingObjectTypes
  } = useQuery(GET_ALL_OBJECT_TYPES);
  const {
    data: fasData,
    loading: loadingFas
  } = useQuery(GET_ENROLLABLE_OBJECT_TYPES_FAS);
  const loading = loadingAccess || loadingObjectTypes || loadingFas;
  const options = useMemo(() => {
    var _objectTypeData$enrol, _fasData$enrollableOb;
    if (loading) {
      return [];
    }
    const allObjectTypes = (_objectTypeData$enrol = objectTypeData === null || objectTypeData === void 0 ? void 0 : objectTypeData.enrollment_allObjectTypes) !== null && _objectTypeData$enrol !== void 0 ? _objectTypeData$enrol : EMPTY_OBJECT_TYPES;
    const hubspotDefinedObjectTypeConfigs = Object.values((_fasData$enrollableOb = fasData === null || fasData === void 0 ? void 0 : fasData.enrollableObjectTypesFAS) !== null && _fasData$enrollableOb !== void 0 ? _fasData$enrollableOb : EMPTY_ENROLLABLE_OBJECT_TYPE).filter(({
      objectTypeId
    }) => !!accessByObjectTypeId[objectTypeId] && allObjectTypes.has(objectTypeId)).map(getHubSpotDefinedObjectTypeConfig);
    const customObjectTypeConfigs = allObjectTypes.valueSeq().filter(({
      metaTypeId
    }) => WORKFLOWS_ALLOWED_CUSTOM_OBJECT_META_TYPE_IDS.includes(metaTypeId !== null && metaTypeId !== void 0 ? metaTypeId : NaN)).map(definition => getCustomObjectTypeConfig(definition.getObjectTypeId())).toArray();
    const appObjectTypeConfigs = allObjectTypes.valueSeq().filter(({
      metaTypeId
    }) => metaTypeId === INTEGRATION).map(definition => getAppObjectTypeConfig(definition.getObjectTypeId())).filter(config => config !== null).toArray();
    const selectableObjectTypeConfigs = [...hubspotDefinedObjectTypeConfigs, ...appObjectTypeConfigs, ...customObjectTypeConfigs].filter(({
      objectTypeId
    }) => objectTypeFilter(objectTypeId)).sort(({
      objectTypeId: typeIdA
    }, {
      objectTypeId: typeIdB
    }) => compareObjectTypeIds(typeIdA, typeIdB));
    return selectableObjectTypeConfigs.map(({
      objectTypeId,
      badge,
      upgradeModalKey,
      CustomModal
    }) => {
      var _objectType$singularF;
      const objectType = allObjectTypes.get(objectTypeId);
      const access = accessByObjectTypeId[objectTypeId];
      const hidden = (access === null || access === void 0 ? void 0 : access.accessLevel) === 'NO_ACCESS';
      const disabled = (access === null || access === void 0 ? void 0 : access.accessLevel) === 'PORTAL_ACCESS';
      const locked = (access === null || access === void 0 ? void 0 : access.accessLevel) === 'UPGRADE_ACCESS';
      let badgeKey = '';
      if (badge === 'beta') {
        badgeKey = 'automation-ui-enrollment.workflowTypeSelect.badge.beta';
      } else if (badge === 'new') {
        badgeKey = 'automation-ui-enrollment.workflowTypeSelect.badge.new';
      }
      return {
        text: (_objectType$singularF = objectType === null || objectType === void 0 ? void 0 : objectType.singularForm) !== null && _objectType$singularF !== void 0 ? _objectType$singularF : '',
        value: objectTypeId,
        hidden,
        disabled,
        locked,
        badge: locked ?
        /*#__PURE__*/
        // The extra span here fixes an alignment issue
        _jsx("span", {
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "locked"
          })
        }) : badge && {
          use: badge,
          text: I18n.text(badgeKey)
        },
        upgradeModalKey,
        CustomModal
      };
    }).filter(({
      hidden
    }) => !(hidden !== null && hidden !== void 0 ? hidden : false));
  }, [loading, objectTypeData, fasData, accessByObjectTypeId, objectTypeFilter]);
  return {
    loading,
    options
  };
}